import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CREDIT_ENFORCEMENT_DEFS,
  CREDIT_ENFORCEMENT_MAP,
  type CreditEnforcement,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { useTenantProfile } from '../../lib/settings';
import { Icon } from '../../components/Icon';
import { PageHead } from '../../components/ui';

/**
 * قواعدُ الائتمان — في الإعدادات لا في شاشة المتابعة.
 *
 * درجةُ الإلزام والسقفُ الافتراضيّ يُضبطان مرّةً حين تُقرَّر سياسةُ
 * الشركة، ومراجعةُ المتجاوزين عملٌ يوميّ. وكلُّ ضبطٍ في مكانٍ واحد: من
 * أراد تغييرَ قاعدةٍ يعرف أين يجدها.
 */
export function CreditRules() {
  const queryClient = useQueryClient();
  const profile = useTenantProfile();

  const [mode, setMode] = useState<CreditEnforcement>('OFF');
  const [limit, setLimit] = useState('0');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const s = profile.data?.settings;
    if (!s || dirty) return;
    setMode((s.creditEnforcement as CreditEnforcement) ?? 'OFF');
    setLimit(String(s.creditLimitDefault ?? 0));
  }, [profile.data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      api('/settings/preferences', {
        method: 'PATCH',
        body: { creditEnforcement: mode, creditLimitDefault: Number(limit) || 0 },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      await queryClient.invalidateQueries({ queryKey: ['credit'] });
      setDirty(false);
    },
  });

  return (
    <div className="page">
      <PageHead
        title="قواعد الائتمان"
        subtitle="ما يحدث حين يتجاوز عميلٌ سقفَه — والسقفُ لمن لم يُحدَّد له"
        actions={
          <Link to="/finance/credit" className="btn">
            <Icon name="alert" size={15} />
            متابعة الائتمان
          </Link>
        }
      />

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">درجة الإلزام والسقف الافتراضي</h2>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </div>

        <div className="card-body">
          {save.isError && (
            <div className="alert alert-danger mb-4">
              {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
            </div>
          )}

          <div className="form-grid">
            <div className="field span-2">
              <label className="label" htmlFor="ce-mode">
                درجة الإلزام
              </label>
              <select
                id="ce-mode"
                className="select"
                value={mode}
                onChange={(e) => {
                  setMode(e.target.value as CreditEnforcement);
                  setDirty(true);
                }}
              >
                {CREDIT_ENFORCEMENT_DEFS.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label}
                  </option>
                ))}
              </select>
              <span className="tiny mute">{CREDIT_ENFORCEMENT_MAP[mode].description}</span>
            </div>

            <div className="field">
              <label className="label" htmlFor="ce-limit">
                السقف الافتراضي
              </label>
              <input
                id="ce-limit"
                className="input num"
                type="number"
                min="0"
                step="1000"
                value={limit}
                onChange={(e) => {
                  setLimit(e.target.value);
                  setDirty(true);
                }}
              />
              <span className="tiny mute">
                يُطبَّق على من لم يُحدَّد له سقف. صفر يعني ألّا سقف افتراضي.
              </span>
            </div>
          </div>

          {mode === 'BLOCK' && (
            <div className="alert alert-warning mt-4">
              راجع <Link to="/finance/credit">قائمة المتجاوزين</Link> قبل الحفظ: بعد المنع لا تُرسل
              لهم فاتورة إلا بتخويل من الإدارة.
            </div>
          )}

          <div className="alert alert-info mt-4">
            سقف كل عميل يُضبط في ملفه بإدارة العملاء. الصفر هناك يعني «نقدي فقط» لا «بلا حد».
          </div>
        </div>
      </div>
    </div>
  );
}
